import { useContext, useState } from 'react';
import { GlobalContext } from '../../../context/GlobalContext';
import { unsafeAPI, TableRequest } from '../../../hooks/api';
import { Dialog } from '../../../components/Dialog';
import { Button } from '../../../shadcn/Button';
import Notice from '../../../components/Notice';
import { ShieldCheck } from 'lucide-react';
import { API_BASE } from '../../../utils/constants';
import TOTPAuth from '../../../components/TOTP';

interface FetchAllConfig {
	statusFilter: string | null;
	dateFilter: { from?: string; to?: string };
	excludeEmails: string[];
	totalCount: number;
}

interface ApprovePayoutsDialogProps {
	payoutIds: string[];
	fetchAllConfig?: FetchAllConfig;
	totalAmountUsd?: number;
	totalAmountBs?: number;
	bcvRate?: number;
	country?: string;
	onClose: () => void;
	onActionComplete: () => void;
}

interface ApproveResponse {
	success: boolean;
	approved_count?: number;
	approved_ids?: string[];
	skipped?: { payout_id: string; reason: string }[];
	run_id?: string;
	detail?: string;
}

export default function ApprovePayoutsDialog({ payoutIds, fetchAllConfig, totalAmountUsd, totalAmountBs, bcvRate, country, onClose, onActionComplete }: ApprovePayoutsDialogProps) {
	const { userData, internalCountry } = useContext(GlobalContext);

	const [loading, setLoading] = useState(false);
	const [fetchProgress, setFetchProgress] = useState<{ fetched: number; total: number } | null>(null);
	const [result, setResult] = useState<ApproveResponse | null>(null);
	const [error, setError] = useState<string | null>(null);

	const fetchAllPayoutIds = async (): Promise<string[]> => {
		if (!fetchAllConfig) return payoutIds;

		const allIds: string[] = [];
		let lastObjectId: string | null = null;
		const batchSize = 500;

		while (true) {
			const payload: TableRequest = {
				master_store_email: null,
				last_object_id: lastObjectId,
				batch_size: batchSize,
				filter_by: [],
			};

			if (fetchAllConfig.statusFilter && fetchAllConfig.statusFilter !== 'all') {
				payload.filter_by!.push({ column_name: 'Estado', value: fetchAllConfig.statusFilter });
			}

			if (fetchAllConfig.dateFilter.from && fetchAllConfig.dateFilter.to) {
				payload.date_range = {
					from_date: fetchAllConfig.dateFilter.from,
					to_date: fetchAllConfig.dateFilter.to,
				};
			}

			const res = await unsafeAPI(
				'POST',
				`${API_BASE}/tables/merchant_payouts/`,
				payload,
				userData,
				internalCountry?.get()
			);

			if (!res?.column_names || !res?.rows) break;

			const idColIndex = res.column_names.indexOf('ID Payout');
			const emailColIndex = res.column_names.indexOf('Email del Aliado');

			if (idColIndex === -1) break;

			for (const row of res.rows) {
				const email = emailColIndex !== -1 ? String(row[emailColIndex]) : '';
				if (fetchAllConfig.excludeEmails.length > 0 && fetchAllConfig.excludeEmails.includes(email)) {
					continue;
				}
				allIds.push(String(row[idColIndex]));
			}

			setFetchProgress({ fetched: allIds.length, total: fetchAllConfig.totalCount });

			if (!res.last_object_id || res.rows.length < batchSize) break;
			lastObjectId = res.last_object_id;
		}

		return allIds;
	};

	const handleApprove = async (code: string) => {
		setLoading(true);
		setError(null);

		try {
			let idsToApprove = payoutIds;

			if (fetchAllConfig) {
				setFetchProgress({ fetched: 0, total: fetchAllConfig.totalCount });
				idsToApprove = await fetchAllPayoutIds();
				setFetchProgress(null);

				if (idsToApprove.length === 0) {
					setError('No se encontraron payouts para aprobar.');
					setLoading(false);
					return;
				}
			}

			const response: ApproveResponse = await unsafeAPI(
				'POST',
				`${API_BASE}/payments/merchant_payouts/approve/`,
				{
					payout_ids: idsToApprove,
					confirmation_code: code,
					test_mode: false,
				},
				userData,
				internalCountry?.get()
			);

			if (response?.success) {
				setResult(response);
			} else {
				setError(response?.detail || 'Error al aprobar los payouts');
			}
		} catch (err: any) {
			setError('Error: ' + err.message);
		} finally {
			setLoading(false);
		}
	};

	const handleClose = () => {
		if (result?.success) onActionComplete();
		else onClose();
	};

	const displayCount = fetchAllConfig ? fetchAllConfig.totalCount : payoutIds.length;

	return (
		<Dialog title="Aprobar Payouts" onClose={handleClose} className="max-w-lg" noContentWrapper cerrarButton={false}>
			<div className="no-scrollbar space-y-4 overflow-y-auto p-1 leading-normal text-neutral-200">
				<div className="flex items-center gap-3 rounded-lg bg-amber-500/10 px-3 py-2">
					<ShieldCheck className="size-5 shrink-0 text-amber-400" />
					<div className="text-sm text-amber-300">
						<p>
							Se aprobarán <strong>{displayCount}</strong> payout{displayCount !== 1 ? 's' : ''} para
							ejecución.
						</p>
						{totalAmountUsd != null && totalAmountUsd > 0 && (
							<p className="mt-1 font-semibold">
								Total: $
								{totalAmountUsd.toLocaleString('en', {
									minimumFractionDigits: 2,
									maximumFractionDigits: 2,
								})}
								{country === 'venezuela' && bcvRate != null && bcvRate > 1 && totalAmountBs != null && (
									<>
										{' '}
										/ Bs{' '}
										{totalAmountBs.toLocaleString('en', {
											minimumFractionDigits: 2,
											maximumFractionDigits: 2,
										})}
									</>
								)}
							</p>
						)}
						{fetchAllConfig && fetchAllConfig.excludeEmails.length > 0 && (
							<span className="block mt-1 text-amber-400/70">
								({fetchAllConfig.excludeEmails.length} email
								{fetchAllConfig.excludeEmails.length !== 1 ? 's' : ''} excluido
								{fetchAllConfig.excludeEmails.length !== 1 ? 's' : ''})
							</span>
						)}
					</div>
				</div>

				{!result ? (
					<div className="flex flex-col gap-2">
						<TOTPAuth isSetUp={true} onSuccess={handleApprove} callVerifyEndpoint={false} />

						{fetchProgress && (
							<div className="space-y-2">
								<div className="flex items-center gap-2 text-sm text-neutral-400">
									<div className="h-4 w-4 animate-spin rounded-full border-2 border-neutral-600 border-t-primary-500" />
									Obteniendo payouts... ({fetchProgress.fetched}/{fetchProgress.total})
								</div>
								<div className="h-1.5 w-full overflow-hidden rounded-full bg-neutral-800">
									<div
										className="h-full rounded-full bg-primary-500 transition-all duration-300"
										style={{ width: `${Math.min((fetchProgress.fetched / fetchProgress.total) * 100, 100)}%` }}
									/>
								</div>
							</div>
						)}

						{error && <Notice message={error} variant="error" />}

						{loading && !fetchProgress && (
							<div className="flex items-center gap-2 text-sm text-neutral-400">
								<div className="h-4 w-4 animate-spin rounded-full border-2 border-neutral-600 border-t-primary-500" />
								Aprobando payouts...
							</div>
						)}

						<div className="flex justify-end border-t border-neutral-800 pt-4">
							<Button variant="secondary" className="w-fit" onClick={onClose} disabled={loading}>
								Cancelar
							</Button>
						</div>
					</div>
				) : (
					<>
						<Notice
							message={`${result.approved_count ?? displayCount} payout${(result.approved_count ?? displayCount) !== 1 ? 's' : ''} aprobado${(result.approved_count ?? displayCount) !== 1 ? 's' : ''} correctamente.${result.run_id ? ` ID: ${result.run_id}` : ''}`}
							variant="success"
						/>

						{result.skipped && result.skipped.length > 0 && (
							<div className="space-y-2">
								<p className="text-sm font-medium text-neutral-300">
									Omitidos ({result.skipped.length}):
								</p>
								<div className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-neutral-800 bg-neutral-900/50 p-3">
									{result.skipped.map((item, idx) => (
										<p key={idx} className="text-xs text-neutral-400">
											<span className="font-mono text-neutral-300">{item.payout_id.slice(0, 8)}...</span>
											{' — '}
											{item.reason}
										</p>
									))}
								</div>
							</div>
						)}

						<div className="flex justify-end border-t border-neutral-800 pt-4">
							<Button variant="primary" className="w-fit" onClick={handleClose}>
								Cerrar
							</Button>
						</div>
					</>
				)}
			</div>
		</Dialog>
	);
}
